import { useEffect, useState } from "react";
import { Play } from "lucide-react";
import { StaggerGroup, StaggerItem } from "@/components/Reveal";
import { VideoModal } from "@/components/VideoModal";
import { fetchAssociations } from "@/lib/api";

const TABS = ["All", "Out-Film", "In-Film", "In-Song", "Event"];

export default function MovieIntegrations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("All");

  useEffect(() => {
    fetchAssociations().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  // Pin featured cards to the top; the rest keep their existing order.
  const base = tab === "All" ? items : items.filter((a) => (a.category || "Out-Film") === tab);
  const filtered = base.slice().sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  return (
    <div>
      <section className="pt-28 md:pt-36 pb-8 md:pb-10 container-x" data-testid="associations-heading">
        <h1 className="font-display uppercase text-offwhite leading-[0.92] tracking-tight text-[15vw] sm:text-[12vw] md:text-8xl lg:text-[7vw]">
          Brand Integrations
        </h1>
      </section>

      {/* Category filter tabs */}
      <div className="container-x flex flex-wrap gap-3 mb-14 md:mb-20" data-testid="association-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`tab-${t.toLowerCase()}`}
            className={`px-6 py-2.5 rounded-full uppercase tracking-[0.2em] text-xs font-semibold transition-all duration-300 ${
              tab === t
                ? "bg-gold text-void border border-gold"
                : "border border-gold/40 text-gold hover:bg-gold/10 hover:border-gold"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <section className="pb-16 md:pb-20 container-x" data-testid="associations-grid">
        {loading ? (
          <p className="text-white/50">Loading associations…</p>
        ) : filtered.length === 0 ? (
          <p className="text-white/50">No associations in this category yet.</p>
        ) : (
          <StaggerGroup key={tab} instant className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((a) => (
              <StaggerItem key={a.id}>
                <button
                  onClick={() => setActive(a)}
                  data-testid={`association-${a.id}`}
                  data-cursor="hover"
                  className="group relative block w-full text-left overflow-hidden aspect-video border border-white/10"
                >
                  {a.thumbnail ? (
                    <img src={a.thumbnail} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-surface to-void" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-void via-void/30 to-transparent" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-16 h-16 rounded-full border border-gold/60 bg-void/40 backdrop-blur-sm flex items-center justify-center group-hover:bg-gold group-hover:border-gold transition-colors duration-300">
                      <Play size={22} className="text-gold group-hover:text-void ml-0.5 transition-colors" fill="currentColor" />
                    </span>
                  </span>
                  {a.featured && <span className="absolute top-4 left-4 text-[0.6rem] uppercase tracking-widest text-void bg-gold px-2 py-1">Featured</span>}
                  <div className="absolute left-4 bottom-4 max-w-[85%]">
                    <div className="inline-block bg-gold px-3.5 py-2 rounded-md shadow-[0_8px_24px_rgba(0,0,0,0.45)]">
                      <p className="text-[0.62rem] tracking-[0.22em] uppercase text-void font-bold">{a.brand}{a.year ? ` · ${a.year}` : ""}</p>
                      <h3 className="mt-0.5 font-display text-lg md:text-xl uppercase text-void leading-none">{a.title}</h3>
                    </div>
                  </div>
                </button>
              </StaggerItem>
            ))}
          </StaggerGroup>
        )}
      </section>

      <VideoModal open={!!active} url={active?.videoUrl} title={active ? `${active.brand}${active.title ? ` × ${active.title}` : ""}` : ""} onClose={() => setActive(null)} />
    </div>
  );
}
